import { useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Download, BookOpen, LayoutDashboard } from "lucide-react";

import { useSetBreadcrumbs } from "@/NavBar/NavigationLayoutContext";
import PageWithTitle from "@/PageWithTitle";
import { Button } from "@/design-system/components/Button/Button";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { ROUTES } from "./constants";
import { MoneySavedProvider, useMoneySaved } from "./MoneySavedContext";
import { MoneyHero } from "./MoneyHero";
import { MoneyStatCardGrid } from "./MoneyStatCardGrid";
import { MoneyMonthlySavings } from "./MoneyMonthlySavings";
import { ScrollSection } from "./ScrollSection";
import { EstimatedSavingsBand } from "./EstimatedSavingsBand";

const breadcrumbs = [
  {
    label: "Internal Tools",
    disabled: true,
  },
  {
    label: "Money Saved",
    href: ROUTES.MONEY_SAVED_HOME,
  },
];

function PageHeader({
  storyMode,
  onToggleStoryMode,
  isGeneratingPdf,
  onDownloadPdf,
}: {
  storyMode: boolean;
  onToggleStoryMode: () => void;
  isGeneratingPdf: boolean;
  onDownloadPdf: () => void;
}) {
  return (
    <div className="flex flex-col items-start justify-between gap-2 lg:flex-row">
      <div className="animate-fade-in-up flex flex-col gap-2">
        <h1 className="transition-colors hover:text-blue-600">
          Money Saved with Freshpaint
        </h1>
        <p className="text-textSubtle">
          Track your savings as you optimize your marketing spend.
        </p>
      </div>
      <div className="animate-fade-in-up flex items-center gap-2">
        <Button
          variant="outline"
          leadingIcon={
            storyMode ? <LayoutDashboard size={16} /> : <BookOpen size={16} />
          }
          onClick={onToggleStoryMode}
          className="cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg active:scale-[0.97]"
        >
          {storyMode ? "Dashboard" : "Story Mode"}
        </Button>
        <Button
          leadingIcon={
            isGeneratingPdf ? (
              <Download size={16} className="animate-spin-smooth" />
            ) : (
              <Download size={16} />
            )
          }
          onClick={onDownloadPdf}
          disabled={isGeneratingPdf}
          className="cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg active:scale-[0.97] print:hidden"
        >
          {isGeneratingPdf ? "Generating..." : "Download PDF"}
        </Button>
      </div>
    </div>
  );
}

function DashboardLayout() {
  return (
    <div className="flex flex-col gap-12">
      <MoneyHero />
      <MoneyStatCardGrid />
      <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
      <MoneyMonthlySavings />
    </div>
  );
}

function StoryLayout() {
  return (
    <>
      {/* Section 1: Total Savings Hero */}
      <ScrollSection className="bg-gradient-to-b from-slate-50/80 via-white to-blue-50/30">
        <MoneyHero showExplainability={false} />
        <p className="text-textSubtle animate-fade-in-up mx-auto mt-4 max-w-lg text-center text-lg leading-relaxed">
          Every dollar here is money your ad platforms would have wasted without
          Freshpaint&rsquo;s server-side event optimization.
        </p>
      </ScrollSection>

      {/* Section 2: Estimated Savings Cinematic */}
      <EstimatedSavingsBand />

      {/* Section 3: Monthly Breakdown */}
      <ScrollSection>
        <MoneyStatCardGrid showNarrative />
      </ScrollSection>

      {/* Section 4: CPA Trend + Chart */}
      <ScrollSection>
        <MoneyMonthlySavings showNarrative />
      </ScrollSection>
    </>
  );
}

function MoneySavedContent() {
  const { isLoading, error, totalSavings, months } = useMoneySaved();
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [storyMode, setStoryMode] = useState(false);

  const latestMonth = months.length > 0 ? months[months.length - 1] : null;

  const currentMonthSavings = latestMonth?.savings ?? 0;

  const avgMonthlySavings = useMemo(() => {
    if (months.length === 0) return 0;
    const total = months.reduce((sum, m) => sum + m.savings, 0);
    return Math.round(total / months.length);
  }, [months]);

  const handleDownloadPdf = useCallback(async () => {
    setIsGeneratingPdf(true);
    try {
      const [{ pdf }, { MoneySavedPdf }] = await Promise.all([
        import("@react-pdf/renderer"),
        import("./MoneySavedPdf"),
      ]);

      const doc = (
        <MoneySavedPdf
          totalSavings={totalSavings}
          monthlySavings={months}
          currentMonthSavings={currentMonthSavings}
          avgMonthlySavings={avgMonthlySavings}
        />
      );

      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `freshpaint-savings-report-${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to generate PDF:", err);
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [totalSavings, months, currentMonthSavings, avgMonthlySavings]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner className="size-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full flex-col gap-12">
        <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
          <h1>Money Saved with Freshpaint</h1>
        </div>
        <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-red-200 bg-red-50 p-8 text-center">
          <h2 className="text-red-800">Unable to load</h2>
          <p className="text-textSubtle max-w-md">
            Failed to load cost savings data. Please try again later.
          </p>
          <Button onClick={() => window.location.reload()}>Refresh page</Button>
        </div>
      </div>
    );
  }

  if (storyMode) {
    return createPortal(
      <div className="z-overlay fixed inset-0 overflow-y-auto scroll-smooth bg-white">
        <div className="fixed top-0 right-0 left-0 z-60 bg-white/80 p-6 backdrop-blur-sm">
          <div className="mx-auto max-w-[960px]">
            <PageHeader
              storyMode={storyMode}
              onToggleStoryMode={() => setStoryMode(false)}
              isGeneratingPdf={isGeneratingPdf}
              onDownloadPdf={handleDownloadPdf}
            />
          </div>
        </div>
        <StoryLayout />
      </div>,
      document.body,
    );
  }

  return (
    <div className="mb-8 flex min-h-full flex-col gap-12 scroll-smooth bg-gradient-to-b from-slate-50/80 via-white to-blue-50/30">
      <PageHeader
        storyMode={storyMode}
        onToggleStoryMode={() => setStoryMode(true)}
        isGeneratingPdf={isGeneratingPdf}
        onDownloadPdf={handleDownloadPdf}
      />
      <DashboardLayout />
    </div>
  );
}

export function MoneySavedPage() {
  useSetBreadcrumbs(breadcrumbs);

  return (
    <MoneySavedProvider>
      <PageWithTitle title="Money Saved">
        <MoneySavedContent />
      </PageWithTitle>
    </MoneySavedProvider>
  );
}
